import React, { useEffect, useMemo, useRef } from "react";
import "./style.css";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  HiOutlineCalendar,
  HiOutlineTruck,
  HiOutlineArrowLeft,
} from "react-icons/hi";
import Step4 from "./Step4";
import RegPlate from "../../components/RegPlate";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { scrollTop } from "../../utils/window";
import { formatStepDisplayValue } from "../../utils/formating";
import OverLayLoader from "../../components/OverLayLoader";
import { TOASTER_SHOW, toaster_s } from "../../constant";
import { appointmentBelongsToEvaluation } from "../../utils/appointmentBooking";
import { resolveEvaluationPlateCountry } from "../../utils/plateCountry";

const HERO_IMAGE = "/images/hero-book-appointment.jpg";

function getEvaluationReg(evaluation, step2) {
  const reg = step2?.reg_number ?? evaluation?.reg_number ?? evaluation?.car;
  if (reg == null || reg === "" || typeof reg === "object") {
    return "";
  }
  return String(reg).trim().toUpperCase();
}

function canAccessBooking({ step1, currentStep, evaluation, step1complete }) {
  const hasWizardVehicle = Boolean(step1?.variant?.isSelected && currentStep >= 2);
  const hasEvaluationVehicle = Boolean(
    evaluation?.id &&
      (step1complete || evaluation.brand_name || evaluation.model_name)
  );

  return hasWizardVehicle || hasEvaluationVehicle;
}

function EvaluationAppoitmnt(props) {
  const { feedback } = useContext(FeedbackContext);
  const evaluation_page = feedback.evaluation_page || {};
  const { step1, step2, currentStep, evaluation, step1complete } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const bookingSuccessNotified = useRef(false);
  const openedAt = useRef(Date.now());

  const canBook = canAccessBooking({ step1, currentStep, evaluation, step1complete });
  const evaluationId = evaluation?.id;
  const hasConfirmedAppointment = Boolean(
    (Array.isArray(evaluation?.appointment) &&
      evaluation.appointment.some((item) =>
        appointmentBelongsToEvaluation(item, evaluationId)
      )) ||
      appointmentBelongsToEvaluation(props.appointment_create, evaluationId)
  );
  const justSavedAppointment = Boolean(
    props.last_appointment_save &&
      props.last_appointment_save >= openedAt.current
  );
  const isComplete = hasConfirmedAppointment || justSavedAppointment;

  const inspectionTips = [
    evaluation_page.What_happens_text1,
    evaluation_page.What_happens_text2,
    evaluation_page.What_happens_text3,
    evaluation_page.What_happens_text4,
    evaluation_page.What_happens_text5,
  ]
    .map((text) => (text || "").trim())
    .filter(Boolean);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    if (!isComplete || bookingSuccessNotified.current) return undefined;
    bookingSuccessNotified.current = true;
    dispatch({
      type: TOASTER_SHOW,
      payload: {
        show: true,
        autoHide: true,
        type: toaster_s,
        heading: "Appointment booked",
        description:
          "Your inspection appointment is confirmed. Continue on Car Steps.",
      },
    });
    const next = evaluationId ? `/carsteps/${evaluationId}` : "/carsteps";
    const timer = window.setTimeout(() => {
      history.replace(next);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isComplete, dispatch, evaluationId, history]);

  useEffect(() => {
    if (isComplete) return undefined;
    if (!canBook) {
      history.replace("/carsteps");
    }
    return undefined;
  }, [canBook, isComplete, history]);

  const vehicleSummary = useMemo(() => {
    const reg = getEvaluationReg(evaluation, step2);
    const title = [
      evaluation?.brand_name || formatStepDisplayValue(step1?.brand),
      evaluation?.model_name || formatStepDisplayValue(step1?.model),
      evaluation?.year_name || formatStepDisplayValue(step1?.year),
    ]
      .filter(Boolean)
      .join(" ");

    const meta = [
      evaluation?.variant_name || formatStepDisplayValue(step1?.variant),
      evaluation?.fuel_type || formatStepDisplayValue(step1?.fuel_type),
      evaluation?.Mileage ? `${evaluation.Mileage} km` : formatStepDisplayValue(step2?.mileage),
    ]
      .filter(Boolean)
      .join(" · ");

    return { reg, title, meta };
  }, [evaluation, step1, step2]);

  const plateCountry = resolveEvaluationPlateCountry({
    plateCountry: step2?.plate_country,
    evaluation,
    reg: vehicleSummary.reg,
  });

  if (!canBook || isComplete) {
    return <OverLayLoader alwaysShow />;
  }

  return (
    <div className="pi-book-appointment">
      <header className="pi-inner-hero">
        <div className="pi-inner-hero__media" aria-hidden>
          <img
            className="pi-inner-hero__media-img"
            src={`${process.env.PUBLIC_URL}${HERO_IMAGE}`}
            alt=""
          />
          <div className="pi-inner-hero__media-shade" />
        </div>
        <div className="pi-inner-hero__fx" aria-hidden>
          <div className="pi-inner-hero__glow pi-inner-hero__glow--a" />
          <div className="pi-inner-hero__glow pi-inner-hero__glow--b" />
          <div className="pi-inner-hero__grid" />
        </div>
        <div className="pi-inner-hero__content">
          <div className="pi-inner-hero__copy">
            <p className="pi-label pi-inner-hero__label">Book inspection</p>
            <h1 className="pi-inner-hero__title">Schedule your appointment</h1>
            <p className="pi-inner-hero__lead">
              Choose an inspection centre, pick a date and time, and confirm your
              booking in a few steps.
            </p>
          </div>
        </div>
      </header>

      <section className="pi-book-appointment__body">
        <div className="pi-book-appointment__inner">
          <div className="pi-book-appointment__toolbar">
            <button
              type="button"
              className="pi-book-appointment__back"
              onClick={() => history.push("/carsteps")}
            >
              <HiOutlineArrowLeft size={18} aria-hidden />
              <span>Back to car steps</span>
            </button>
          </div>

          <div className="pi-book-appointment__stage">
            <aside className="pi-book-appointment__aside">
              <div className="pi-book-appointment__summary-card">
                <span className="pi-book-appointment__eyebrow">
                  <HiOutlineTruck size={16} aria-hidden />
                  Your vehicle
                </span>
                <h2>{vehicleSummary.title || "Vehicle"}</h2>
                {vehicleSummary.reg ? (
                  <RegPlate
                    value={vehicleSummary.reg}
                    country={plateCountry}
                    className="pi-book-appointment__reg"
                  />
                ) : null}
                {vehicleSummary.meta ? (
                  <p className="pi-book-appointment__summary-meta">{vehicleSummary.meta}</p>
                ) : null}
              </div>

              {inspectionTips.length > 0 ? (
                <div className="pi-book-appointment__info-card pi-evaluation__info-card">
                  <h3>
                    {(evaluation_page.What_happens_inspection || "What happens at inspection").trim()}
                  </h3>
                  <ol className="pi-evaluation__info-list">
                    {inspectionTips.map((text, tipIndex) => (
                      <li key={`tip-${tipIndex}`}>
                        <span className="pi-evaluation__info-num" aria-hidden>
                          {tipIndex + 1}
                        </span>
                        <span className="pi-evaluation__info-text">{text}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </aside>

            <div className="pi-book-appointment__panel">
              <header className="pi-book-appointment__panel-head">
                <span className="pi-book-appointment__eyebrow pi-book-appointment__eyebrow--accent">
                  <HiOutlineCalendar size={16} aria-hidden />
                  {evaluation_page.Your_appointment_text || "Your appointment"}
                </span>
                <h2>Pick a centre, date &amp; time</h2>
                <p>
                  Select where you would like the inspection, then choose an available
                  slot from the calendar.
                </p>
              </header>
              <div className="pi-book-appointment__form-shell">
                <Step4 formanimationClassname="pi-book-appointment__animate-in" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const mapStateToProps = (state) => ({
  step1: state.sellMyCar.step1,
  step2: state.sellMyCar.step2,
  currentStep: state.sellMyCar.currentStep,
  evaluation: state.sellMyCar.evaluation_create,
  appointment_create: state.sellMyCar.appointment_create,
  last_appointment_save: state.sellMyCar.last_appointment_save,
  step1complete: state.sellMyCar.step1complete,
  loading_appointment_create: state.sellMyCar.loading_appointment_create,
});

export default connect(mapStateToProps)(EvaluationAppoitmnt);
